import { featureNames, Msg, useFormatMessage } from "lib/localization";
import { contentLabel } from "lib/styleguide/base.scss";
import React, { Fragment } from "react";
import { defineMessages, FormattedMessage as T } from "react-intl";
import customCues from "./images/custom-cues.png";
import customCues2x from "./images/custom-cues@2x.png";
import customCues3x from "./images/custom-cues@3x.png";
import eTFeature from "./images/et-feature.png";
import eTFeature2x from "./images/et-feature@2x.png";
import eTFeature3x from "./images/et-feature@3x.png";
import pdfFeature from "./images/pdf-feature.png";
import pdfFeature2x from "./images/pdf-feature@2x.png";
import pdfFeature3x from "./images/pdf-feature@3x.png";
import rideCleanup from "./images/ride-clean-up.png";
import rideCleanup2x from "./images/ride-clean-up@2x.png";
import rideCleanup3x from "./images/ride-clean-up@3x.png";
import routePlanning from "./images/route-planning.png";
import routePlanning2x from "./images/route-planning@2x.png";
import routePlanning3x from "./images/route-planning@3x.png";
import voiceFeature from "./images/voice-feature.png";
import voiceFeature2x from "./images/voice-feature@2x.png";
import voiceFeature3x from "./images/voice-feature@3x.png";
import styles from "./UpsellFooter.scss";

const topContent = {
  premium: (fm) => (
    <Fragment>
      <div className={contentLabel}>
        <T id="UpsellFooter.whyPremium" defaultMessage="why premium" />
      </div>
      <h3 className={styles.footerHeader}>
        <T
          id="UpsellFooter.premiumWhy"
          defaultMessage="Powerful tools for planning high quality routes and managing your ride data in detail or in bulk."
        />
      </h3>
    </Fragment>
  ),
  basic: (fm) => (
    <Fragment>
      <div className={contentLabel}>
        <T id="UpsellFooter.whyBasic" defaultMessage="why basic" />
      </div>
      <h3 className={styles.footerHeader}>
        <T
          id="UpsellFooter.basicWhy"
          defaultMessage="Signature tools for navigating and downloading routes and sharing rides."
        />
      </h3>
    </Fragment>
  ),
};

const leftMessages = defineMessages({
  advancedPlanning: {
    id: "leftMessages.advancedPlanning",
    defaultMessage: "advanced route planning",
  },
});

const leftContent = {
  premium: (fm) => (
    <Fragment>
      <img
        src={routePlanning}
        srcSet={`${routePlanning} 1x, ${routePlanning2x} 2x, ${routePlanning3x} 3x`}
        alt={fm(leftMessages.advancedPlanning)}
      />
      <h4>
        <Msg startCase m={leftMessages.advancedPlanning} />
      </h4>
      <p>
        <T
          id="UpsellFooter.rideCuesheet"
          defaultMessage="Turn recorded rides into a route with a cuesheet. Split, combine, and reverse routes. Display multiple routes while planning so you can easily trace portions of other routes."
        />
      </p>
    </Fragment>
  ),
  basic: (fm) => (
    <Fragment>
      <img
        src={eTFeature}
        srcSet={`${eTFeature} 1x, ${eTFeature2x} 2x, ${eTFeature3x} 3x`}
        alt={fm(featureNames.estimatedTime)}
      />
      <h4>
        <Msg startCase m={featureNames.estimatedTime} />
      </h4>
      <p>
        <T
          id="UpsellFooter.personalEstTime"
          defaultMessage="Know how long a ride is going to take with personalized estimated time for every route you create or find."
        />
      </p>
    </Fragment>
  ),
};

const centerMessages = defineMessages({
  customCues: {
    id: "centerMessages.customCues",
    defaultMessage: "Custom Cues and Points of Interest",
  },
  voiceNav: {
    id: "centerMessages.voiceNav",
    defaultMessage: "Voice Navigation",
  },
});

const centerContent = {
  premium: (fm) => (
    <Fragment>
      <img
        src={customCues}
        srcSet={`${customCues} 1x, ${customCues2x} 2x, ${customCues3x} 3x`}
        alt={fm(centerMessages.customCues)}
      />
      <h4>
        <Msg m={centerMessages.customCues} />
      </h4>
      <p>
        <T
          id="UpsellFooter.personalizeRoutes"
          defaultMessage="Personalize your routes with custom cues, and add points of interest to show route features like food, lodging, viewpoints, or water sources."
        />
      </p>
    </Fragment>
  ),
  basic: (fm) => (
    <Fragment>
      <img
        src={voiceFeature}
        srcSet={`${voiceFeature} 1x, ${voiceFeature2x} 2x, ${voiceFeature3x} 3x`}
        alt={fm(centerMessages.voiceNav)}
      />
      <h4>
        <Msg m={centerMessages.voiceNav} />
      </h4>
      <p>
        <T
          id="UpsellFooter.appVoiceNav"
          defaultMessage="Use the Ride with GPS app to get spoken turn-by-turn directions for your routes. Keep your head up, and enjoy the ride."
        />
      </p>
    </Fragment>
  ),
};

const rightMessages = defineMessages({
  rideCleanup: {
    id: "rightMessages.rideCleanup",
    defaultMessage: "Ride Clean Up",
  },
  mapPDF: {
    id: "rightMessages.mapPDF",
    defaultMessage: "Print Map PDFs",
  },
});

const rightContent = {
  premium: (fm) => (
    <Fragment>
      <img
        src={rideCleanup}
        srcSet={`${rideCleanup} 1x, ${rideCleanup2x} 2x, ${rideCleanup3x} 3x`}
        alt={fm(rightMessages.rideCleanup)}
      />
      <h4>
        <Msg m={rightMessages.rideCleanup} />
      </h4>
      <p>
        <T
          id="UpsellFooter.removeBadData"
          defaultMessage="Remove bad data, join multiple rides together, or exclude portions of your ride file from your metrics. Ride cleanup tools help you make sure your data is always accurate."
        />
      </p>
    </Fragment>
  ),
  basic: (fm) => (
    <Fragment>
      <img
        src={pdfFeature}
        srcSet={`${pdfFeature} 1x, ${pdfFeature2x} 2x, ${pdfFeature3x} 3x`}
        alt={fm(rightMessages.mapPDF)}
      />
      <h4>
        <Msg m={rightMessages.mapPDF} />
      </h4>
      <p>
        <T
          id="UpsellFooter.mapPDF"
          defaultMessage="Generate and print PDF files of any route and cuesheet with just a few clicks. Customize the map zoom level and cuesheet options, and get a printable PDF file in seconds."
        />
      </p>
    </Fragment>
  ),
};

export const UpsellFooter = ({ accountLevel }) => {
  const level = accountLevel === 1 ? "basic" : "premium";
  const fm = useFormatMessage();

  return (
    <div className={styles.UpsellFooter}>
      <div className={styles.top}>
        <div className={styles.middleColumn}>
          {topContent[level](fm)}
          <a href={"/choose_account"}>
            <T id="UpsellFooter.fullPlanCompair" defaultMessage="See Full Plan Comparison" />
          </a>
        </div>
      </div>
      <div className={styles.bottom}>
        <div className={styles.left}>{leftContent[level](fm)}</div>
        <div className={styles.center}>{centerContent[level](fm)}</div>
        <div className={styles.right}>{rightContent[level](fm)}</div>
      </div>
      {level === "premium" && (
        <div className={styles.bottom} style={{ marginTop: "20px" }}>
          <div className={styles.left}>{leftContent.basic(fm)}</div>
          <div className={styles.center}>{centerContent.basic(fm)}</div>
          <div className={styles.right}>{rightContent.basic(fm)}</div>
        </div>
      )}
    </div>
  );
};
